import React, { FC, useMemo, useState } from "react";
import "./WsDepotCard.scss";
import { useActionSheet, useWindows } from "@app/overlay";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { CardHeader } from "@shared/ui/card-header";
import { BusinessList } from "@src/components/business-list";
import {
    BusinessDivisionValue,
    BusinessMeasureValue,
    BusinessStockObjValue,
    formatBusinessDateText,
} from "@src/components/business-data-display";
import { ListItem } from "@src/components/business-list";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { DetailList } from "@shared/ui/detail-row";
import { GroupedMovementItem } from "./helpers/groupMovements";
import { formatStockObjTypeName } from "../../../../../../helpers/business/stockObj/formatStockObjTypeName";
import { CardLoader } from "@src/components/card-loader";
import { wsDepotCardWindows } from "./wsDepotCard.windows";
import { useWsDepotCardMovementsQuery } from "./hooks/useWsDepotCardMovementsQuery";

enum Period {
    month = "month",
    quarter = "quarter",
    year = "year",
    all = "all",
}

function getPeriodLeftBorderDate(period: Period): Date {
    const curDate = new Date();
    switch (period) {
        case Period.month:
            return new Date(curDate.getFullYear(), curDate.getMonth(), 1);
        case Period.quarter:
            return new Date(curDate.getFullYear(), Math.floor(curDate.getMonth() / 3) * 3, 1);
        case Period.year:
            return new Date(curDate.getFullYear(), 0, 1);
        default:
            return new Date(0);
    }
}

const WsDepotCard: FC = () => {
    const cardCode = useRequiredNumberParam("code", "код карточки");
    const windows = useWindows();
    const actionSheet = useActionSheet();
    const depotQuery = useWsDepotCardMovementsQuery(cardCode);

    const data = depotQuery.card;
    const [period, setPeriod] = useState<Period>(Period.all);
    const [showCardInfo, setShowCardInfo] = useState<boolean>(true);

    const groupedMovements = useMemo(() => {
        if (period === Period.all) {
            return depotQuery.movementGroups;
        }

        const leftBorderDate = getPeriodLeftBorderDate(period);
        return depotQuery.movementGroups.filter((group) => new Date(group.startMonthDate) >= leftBorderDate);
    }, [depotQuery.movementGroups, period]);

    const periodSections = useMemo<ActionSheetSection[]>(
        () => [
            {
                id: "period",
                items: [
                    {
                        type: "action",
                        id: "period-all",
                        label: "Весь период",
                        onSelect: () => setPeriod(Period.all),
                    },
                    {
                        type: "action",
                        id: "period-year",
                        label: "Год",
                        onSelect: () => setPeriod(Period.year),
                    },
                    {
                        type: "action",
                        id: "period-quarter",
                        label: "Квартал",
                        onSelect: () => setPeriod(Period.quarter),
                    },
                    {
                        type: "action",
                        id: "period-month",
                        label: "Месяц",
                        onSelect: () => setPeriod(Period.month),
                    },
                ],
            },
        ],
        [],
    );

    const actionSections = useMemo<ActionSheetSection[]>(
        () => [
            {
                id: "view",
                items: [
                    {
                        type: "toggle",
                        id: "show-card-info",
                        label: "Информация о карточке",
                        description: "Отображать доп. информацию о карточке",
                        checked: showCardInfo,
                        onCheckedChange: setShowCardInfo,
                    },
                ],
            },
        ],
        [showCardInfo],
    );

    const openMovementWindow = (movement: GroupedMovementItem): void => {
        if (data == null) {
            return;
        }

        wsDepotCardWindows.move.open(windows, {
            movement,
            cardNumber: data.Num,
            measure: data.Measure,
        });
    };

    if (depotQuery.isFetching || data == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="ws_depot_card_page">
            <CardHeader
                text={`Цеховая номенклатура №${data.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSections }),
                    },
                ]}
            />
            {showCardInfo && (
                <DetailList className="ws_depot_card_inform">
                    <DetailList.Row
                        label={"Подразделение"}
                        value={<BusinessDivisionValue division={data.Division} section={data.DivisionSection} />}
                    />
                    <DetailList.Row label={"Тип ТМЦ"} value={formatDetailValue(formatStockObjTypeName(data.Stockobj))} />
                    <DetailList.Row label={"Тип продукции"} value={data.ProductionType?.Name ?? "Не задано"} />
                    <DetailList.Row label={"Номенклатура"} value={<BusinessStockObjValue stockObj={data.Stockobj} />} />
                </DetailList>
            )}

            <BusinessList
                className="ws_depot_card_move_list"
                isLoad={depotQuery.isFetching}
                hoodBtns={[
                    {
                        text: "Период",
                        srcIcon: "/images/common/options.svg",
                        onClick: () => actionSheet.open({ title: "Период", sections: periodSections }),
                    },
                ]}
            >
                {groupedMovements.map((group, index) => {
                    const isLastGroup = index === groupedMovements.length - 1;
                    return (
                        <React.Fragment key={index}>
                            <div className="line_month_remainer end">
                                <span>Остаток на {formatBusinessDateText(group.endMonthDate)}</span>
                                <span>
                                    <BusinessMeasureValue value={group.endMonthRemainder} measure={data.Measure} />
                                </span>
                            </div>
                            {group.movements.map((move, indexItem) => (
                                <ListItem
                                    key={indexItem}
                                    title={`Движение ${formatBusinessDateText(move.MoveDate)}`}
                                    props={[
                                        {
                                            label: "Документ",
                                            value: move.DocType?.ShortName ?? "",
                                        },
                                        {
                                            label: "Приход",
                                            value: <BusinessMeasureValue value={move.Arrival} measure={data.Measure} />,
                                        },
                                        {
                                            label: "Расход",
                                            value: <BusinessMeasureValue value={move.Leave} measure={data.Measure} />,
                                        },
                                        {
                                            label: "Остаток",
                                            value: <BusinessMeasureValue value={move.Remainder} measure={data.Measure} />,
                                        },
                                    ]}
                                    onClick={() => {
                                        openMovementWindow(move);
                                    }}
                                />
                            ))}
                            {isLastGroup && (
                                <div className="line_month_remainer start">
                                    <span>Остаток на {formatBusinessDateText(group.startMonthDate)}</span>
                                    <span>
                                        <BusinessMeasureValue value={group.startMonthRemainder} measure={data.Measure} />
                                    </span>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </BusinessList>
        </div>
    );
};

export default WsDepotCard;

function formatDetailValue(value: string): string {
    return value === "" ? "Не задано" : value;
}
